import {
  ROOT_NAVIGATION_HISTORY_KEY,
  ROOT_NAVIGATION_HISTORY_VERSION,
  getRootNavigationIdentity,
  getRootNavigationScope,
  isInternalFullPath,
  resolveRootNavigationStart,
} from '../utils/rootNavigationState.mjs'

/**
 * Vue Router history backed by native browser entries whose public URL is '/'.
 * Internal routes remain in the standard top-level history.state fields so the
 * router can restore parameters, scroll positions and cancelled navigations.
 */
export function createRootUrlHistory({ window: win = globalThis.window } = {}) {
  if (!win?.history || !win?.location) throw new Error('Root URL history requires a browser window')

  const nativeHistory = win.history
  const observesNativeNavigation = typeof win.navigation?.addEventListener === 'function'
  const listeners = new Set()
  const queryTraversals = new WeakMap()
  let queryTrackingEnabled = false
  let activeTraversal = null
  let destroyed = false
  let pauseTarget = null
  let lastTraversal = null
  let pendingRollback = null
  let entrySequence = 0
  let navigationKey = win.navigation?.currentEntry?.key || null

  const getScroll = () => ({ left: win.scrollX || 0, top: win.scrollY || 0 })
  const positionOf = (value, fallback) => Number.isInteger(value) && value >= 0 ? value : fallback
  const nextEntryId = () => win.crypto?.randomUUID?.() || `${Date.now()}-${++entrySequence}-${Math.random()}`
  const marker = (entryId = nextEntryId()) => ({
    version: ROOT_NAVIGATION_HISTORY_VERSION,
    scope: getRootNavigationScope(win),
    identity: getRootNavigationIdentity(win),
    entryId,
  })

  function validState(value) {
    const metadata = value?.[ROOT_NAVIGATION_HISTORY_KEY]
    return Boolean(value && typeof value === 'object' &&
      isInternalFullPath(value.current) && Number.isInteger(value.position) && value.position >= 0 &&
      metadata?.version === ROOT_NAVIGATION_HISTORY_VERSION &&
      metadata.scope === getRootNavigationScope(win) &&
      metadata.identity === getRootNavigationIdentity(win))
  }

  const start = resolveRootNavigationStart(win)
  let currentLocation = isInternalFullPath(start) ? start : '/intro'
  const previousState = nativeHistory.state
  let historyState = {
    ...(validState(previousState) ? previousState : {}),
    back: validState(previousState) && isInternalFullPath(previousState.back) ? previousState.back : null,
    current: currentLocation,
    forward: validState(previousState) && isInternalFullPath(previousState.forward) ? previousState.forward : null,
    replaced: true,
    position: positionOf(previousState?.position, nativeHistory.length - 1),
    scroll: validState(previousState) ? previousState.scroll || null : null,
    [ROOT_NAVIGATION_HISTORY_KEY]: marker(previousState?.[ROOT_NAVIGATION_HISTORY_KEY]?.entryId),
  }
  nativeHistory.replaceState(historyState, '', '/')
  navigationKey = win.navigation?.currentEntry?.key || navigationKey
  let confirmedEntry = { state: historyState, navigationKey }

  function confirmCurrentEntry() {
    confirmedEntry = { state: historyState, navigationKey }
  }

  function commit(state, replace) {
    nativeHistory[replace ? 'replaceState' : 'pushState'](state, '', '/')
    historyState = state
    currentLocation = state.current
    navigationKey = win.navigation?.currentEntry?.key || null
  }

  function replace(to, data = {}) {
    if (!isInternalFullPath(to)) throw new TypeError('Invalid internal navigation path')
    pauseTarget = null
    lastTraversal = null
    pendingRollback = null
    const state = {
      ...nativeHistory.state,
      ...historyState,
      ...data,
      back: historyState.back,
      current: to,
      forward: historyState.forward,
      replaced: true,
      position: historyState.position,
      [ROOT_NAVIGATION_HISTORY_KEY]: marker(historyState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId),
    }
    commit(state, true)
    confirmCurrentEntry()
  }

  function push(to, data = {}) {
    if (!isInternalFullPath(to)) throw new TypeError('Invalid internal navigation path')
    pauseTarget = null
    lastTraversal = null
    pendingRollback = null
    const currentState = {
      ...historyState,
      ...nativeHistory.state,
      current: currentLocation,
      position: historyState.position,
      forward: to,
      scroll: getScroll(),
      [ROOT_NAVIGATION_HISTORY_KEY]: historyState[ROOT_NAVIGATION_HISTORY_KEY],
    }
    commit(currentState, true)
    commit({
      ...data,
      back: currentLocation,
      current: to,
      forward: null,
      replaced: false,
      position: currentState.position + 1,
      scroll: null,
      [ROOT_NAVIGATION_HISTORY_KEY]: marker(),
    }, false)
    confirmCurrentEntry()
  }

  function popStateHandler({ state }) {
    if (destroyed) return
    const from = currentLocation
    const fromState = historyState
    const valid = validState(state)
    const to = valid ? state.current : '/intro'
    const position = positionOf(state?.position, fromState.position)
    const nextState = valid ? state : {
      back: null, current: to, forward: null, replaced: true,
      position, scroll: null, [ROOT_NAVIGATION_HISTORY_KEY]: marker(),
    }
    // Invalid or prior-identity entries must never re-enter a previous user's
    // page, and even an externally changed URL is normalized before guards run.
    if (!valid || win.location.pathname !== '/' || win.location.search || win.location.hash) {
      nativeHistory.replaceState(nextState, '', '/')
    }
    currentLocation = to
    historyState = nextState
    navigationKey = win.navigation?.currentEntry?.key || null

    if (pauseTarget && (pauseTarget.silentFromEntry
      ? pauseTarget.silentFromEntry === fromState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId
      : pauseTarget.navigationKey
      ? pauseTarget.navigationKey === navigationKey
      : pauseTarget.position === position && (!pauseTarget.entryId ||
        pauseTarget.entryId === nextState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId))) {
      pauseTarget = null
      lastTraversal = null
      return
    }
    // A child frame's own history traversal leaves the host route unchanged.
    // Do not rerun guards or destroy/remount the external frame in that case.
    if (to === from) {
      if (confirmedEntry.state.current === to) confirmCurrentEntry()
      return
    }

    pauseTarget = null
    const delta = position - fromState.position
    lastTraversal = {
      delta,
      to,
      toPosition: position,
      toEntryId: nextState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId,
    }
    const info = { delta, type: 'pop', direction: delta > 0 ? 'forward' : delta < 0 ? 'back' : 'unknown' }
    // Vue Router resolves a pop synchronously inside its listener, before any
    // component leave guard. Query object identity therefore carries an exact
    // traversal token even when multiple pending pops target the same route.
    const hashIndex = to.indexOf('#')
    const queryIndex = to.indexOf('?')
    const needsQuery = queryIndex < 0 || (hashIndex >= 0 && queryIndex > hashIndex)
    const parseTarget = queryTrackingEnabled && needsQuery
      ? hashIndex < 0 ? `${to}?` : `${to.slice(0, hashIndex)}?${to.slice(hashIndex)}`
      : to
    for (const callback of [...listeners]) {
      const previousTraversal = activeTraversal
      activeTraversal = lastTraversal
      try { callback(parseTarget, from, info) } finally { activeTraversal = previousTraversal }
    }
  }

  /** Wrap RouterOptions.parseQuery to identify pops before beforeRouteLeave. */
  function createQueryParser(parseQuery) {
    queryTrackingEnabled = true
    return (...args) => {
      const query = parseQuery(...args)
      if (activeTraversal && query && typeof query === 'object') queryTraversals.set(query, activeTraversal)
      return query
    }
  }

  /** Report synchronously from router.afterEach failures and router.onError. */
  function reportNavigationFailure(route) {
    if (!pendingRollback) return
    pendingRollback.reportedFailure = true
    let traversal = null
    let currentRoute = typeof route === 'object' ? route : null
    while (currentRoute && !traversal) {
      traversal = queryTraversals.get(currentRoute.query) || null
      currentRoute = currentRoute.redirectedFrom
    }
    const fullPath = typeof route === 'string' ? route : route?.fullPath
    // Vue Router invokes go() before publishing the failed navigation. Wait
    // for that publication to distinguish an older guard's rejection from
    // the latest pop, even when both traversals have exactly the same delta.
    if (!pendingRollback.traversal || (traversal
      ? traversal !== pendingRollback.traversal : pendingRollback.traversal.to !== fullPath)) {
      pendingRollback.cancelled = true
    }
  }

  function restoreEntry(request) {
    if (destroyed || pendingRollback !== request) return
    pendingRollback = null
    if (request.cancelled) return
    if (!request.reportedFailure) {
      // Native steps can include iframe entries, so host positions alone do
      // not identify a direct traversal's destination. User-initiated browser
      // navigation clears this one-shot flag if only a child frame moved.
      pauseTarget = observesNativeNavigation
        ? { silentFromEntry: historyState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId }
        : { position: historyState.position + request.delta }
      nativeHistory.go(request.delta)
      return
    }
    const traversal = request.traversal
    const matchesTraversal = traversal && lastTraversal === traversal &&
      currentLocation === traversal.to && historyState.position === traversal.toPosition &&
      historyState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId === traversal.toEntryId
    if (request.reportedFailure && !matchesTraversal) return
    const source = matchesTraversal ? confirmedEntry : null
    if (source && source.state.position === historyState.position &&
      source.state[ROOT_NAVIGATION_HISTORY_KEY]?.entryId === historyState[ROOT_NAVIGATION_HISTORY_KEY]?.entryId) return
    const sourceDelta = source ? source.state.position - historyState.position : request.delta
    // A malformed entry may have no usable position. Navigation API keys can
    // still restore it exactly; the legacy fallback retains native semantics.
    const rollbackDelta = sourceDelta || request.delta
    pauseTarget = {
      position: source?.state.position ?? historyState.position + request.delta,
      entryId: source?.state[ROOT_NAVIGATION_HISTORY_KEY]?.entryId,
    }
    // Navigation API entries describe the host, whereas history.go() counts
    // joint entries contributed by iframes as well. Traversing to the source
    // key restores a rejected host navigation without guessing frame steps.
    if (source?.navigationKey && typeof win.navigation?.traverseTo === 'function') {
      pauseTarget.navigationKey = source.navigationKey
      try {
        const result = win.navigation.traverseTo(source.navigationKey)
        Promise.resolve(result?.finished).catch(() => {
          // A superseding navigation can abort this traversal. Only fall
          // back while the same rollback is still pending.
          if (pauseTarget?.navigationKey !== source.navigationKey) return
          delete pauseTarget.navigationKey
          if (rollbackDelta) nativeHistory.go(rollbackDelta)
        })
        return
      } catch {
        delete pauseTarget.navigationKey
      }
    }
    if (rollbackDelta) nativeHistory.go(rollbackDelta)
  }

  function go(delta, triggerListeners = true) {
    if (triggerListeners) {
      pendingRollback = null
      pauseTarget = null
      nativeHistory.go(delta)
      return
    }
    const request = { delta, traversal: lastTraversal, reportedFailure: false, cancelled: false }
    pendingRollback = request
    // afterEach/onError run synchronously after Vue Router requests a silent
    // rollback. Direct adapter callers need no report and still traverse.
    Promise.resolve().then(() => restoreEntry(request))
  }

  function nativeNavigationHandler(event) {
    if (pauseTarget?.silentFromEntry
      && (event.userInitiated || event.navigationType !== 'traverse')) pauseTarget = null
  }

  function saveScroll() {
    if (destroyed || !validState(nativeHistory.state)) return
    historyState = { ...nativeHistory.state, scroll: getScroll() }
    nativeHistory.replaceState(historyState, '', '/')
  }

  win.addEventListener('popstate', popStateHandler)
  win.addEventListener('beforeunload', saveScroll, { passive: true })
  win.addEventListener('pagehide', saveScroll, { passive: true })
  if (observesNativeNavigation) win.navigation.addEventListener('navigate', nativeNavigationHandler)

  return {
    base: '',
    get location() { return currentLocation },
    get state() { return historyState },
    push,
    replace,
    go,
    confirmCurrentEntry,
    createQueryParser,
    reportNavigationFailure,
    createHref: () => '/',
    listen(callback) {
      listeners.add(callback)
      return () => listeners.delete(callback)
    },
    destroy() {
      destroyed = true
      listeners.clear()
      pauseTarget = null
      lastTraversal = null
      pendingRollback = null
      win.removeEventListener('popstate', popStateHandler)
      win.removeEventListener('beforeunload', saveScroll)
      win.removeEventListener('pagehide', saveScroll)
      if (observesNativeNavigation) win.navigation.removeEventListener('navigate', nativeNavigationHandler)
    },
  }
}
